import React, { useContext, useState, useEffect, useRef } from "react";
import ThemeContext from "../../../../Context/theme/ThemeContext";
import { BaseURL } from "../../../../Files/BaseURL";
import { CLIENT_ID } from "../../../../Files/PayPal_Cient_Id";
import ConnectCard from "../Withdraws/ConnectCard";
import Swal from 'sweetalert2';

const Setting = () => {
  const { theme, changetheme } = useContext(ThemeContext);
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [connectCardState, setConnectCardState] = useState(false);
  const [scriptLoaded, setScriptLoaded] = useState(false);
  const paypalRef = useRef(null);

  const handleThemeChange = (newTheme) => {
    changetheme(newTheme);
  };

  // Function to fetch user information
  const userInfo = async () => {
    try {
      const authToken = localStorage.getItem("p@token");
      if (!authToken) {
        throw new Error("No authentication token found");
      }

      const res = await fetch(`${BaseURL}/api/payment/account/pay-info`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          "auth-token": authToken,
        }
      });

      if (!res.ok) {
        throw new Error(`HTTP error! status: ${res.status}`);
      }

      const data = await res.json();
      return data;
    } catch (error) {
      console.error("Error fetching payment info:", error);
      setError(error.message);
      return null;
    }
  };

  useEffect(() => {
    const fetchInfo = async () => {
      setLoading(true);
      setError(null);
      const res = await userInfo();
      setInfo(res);
      setLoading(false);
    };
    fetchInfo();
  }, []);

  // Function to delete payment method
  const handleDeletePayment = async (type) => {
    const result = await Swal.fire({
      title: 'Are you sure?',
      text: `You want to delete your ${type} account?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Yes, delete it!'
    });

    if (result.isConfirmed) {
      try {
        setLoading(true);
        const authToken = localStorage.getItem("p@token");
        if (!authToken) {
          Swal.fire(
            'Error!',
            'Authentication failed. Please try logging in again.',
            'error'
          );
          return;
        }

        const response = await fetch(`${BaseURL}/api/payment/account/delete-payment/${type}`, {
          method: 'DELETE',
          headers: {
            'Content-Type': 'application/json',
            "auth-token": authToken,
          }
        });

        const data = await response.json();

        if (!response.ok) {
          throw new Error(data.message || 'Failed to delete payment method. Please try again later.');
        }

        if (data.success) {
          // Reset PayPal script state when PayPal is deleted
          if (type === 'paypal') {
            setScriptLoaded(false);
            // Remove existing PayPal script
            const existingScript = document.querySelector(`script[src="https://www.paypal.com/sdk/js?client-id=${CLIENT_ID}"]`);
            if (existingScript) {
              existingScript.remove();
            }
            // Clear PayPal container
            if (paypalRef.current) {
              paypalRef.current.innerHTML = '';
            }
          }
          // Refresh the payment info
          const updatedInfo = await userInfo();
          setInfo(updatedInfo);

          Swal.fire(
            'Deleted!',
            `Your ${type} account has been deleted successfully.`,
            'success'
          );
        } else {
          throw new Error(data.message || 'Failed to delete payment method. Please try again later.');
        }
      } catch (error) {
        console.error("Error deleting payment method:", error);
        setError(error.message);
        Swal.fire({
          title: 'Error!',
          text: error.message || 'Something went wrong. Please try again later.',
          icon: 'error',
          confirmButtonColor: '#3085d6'
        });
      } finally {
        setLoading(false);
      }
    }
  };

  // Load PayPal script
  useEffect(() => {
    const loadPayPalScript = () => {
      try {
        const existingScript = document.querySelector(`script[src="https://www.paypal.com/sdk/js?client-id=${CLIENT_ID}"]`);
        if (!existingScript) {
          const script = document.createElement("script");
          script.src = `https://www.paypal.com/sdk/js?client-id=${CLIENT_ID}`;
          script.async = true;
          script.onload = () => {
            setScriptLoaded(true);
            window.dispatchEvent(new Event('paypalscriptloaded'));
          };
          script.onerror = () => {
            console.error("Failed to load PayPal script.");
            setError("Failed to load PayPal. Please try again.");
          };
          document.body.appendChild(script);
        } else {
          setScriptLoaded(true);
        }
      } catch (error) {
        console.error("Error during PayPal script loading:", error.message);
        setError(error.message);
      }
    };

    if (!scriptLoaded) {
      loadPayPalScript();
    }
  }, [scriptLoaded]);

  // Setup PayPal buttons
  useEffect(() => {
    if (!scriptLoaded) return;

    const setupPayPal = async () => {
      try {
        const authToken = localStorage.getItem("p@token");
        const response = await fetch(`${BaseURL}/api/payment/account/paypal-tokens`, {
          method: "POST",
          headers: {
            "auth-token": authToken,
            "Content-Type": "application/json",
          }
        });
        const { access_token } = await response.json();

        if (window.paypal && paypalRef.current) {
          // Clear any existing buttons
          paypalRef.current.innerHTML = '';

          window.paypal.Buttons({
            style: {
              layout: 'vertical',
              color: 'blue',
              shape: 'rect',
              label: 'pay'
            },
            createVaultSetupToken: async () => {
              const result = await fetch(`${BaseURL}/api/payment/account/setup-token`, {
                method: "POST",
                headers: {
                  "Authorization": `Bearer ${access_token}`,
                  "Content-Type": "application/json",
                  "auth-token": authToken,
                },
              });
              const response = await result.json();
              return response.id;
            },
            onApprove: async ({ vaultSetupToken }) => {
              await fetch(`${BaseURL}/api/payment/account/payment-token`, {
                method: "POST",
                body: JSON.stringify({ vaultSetupToken }),
                headers: {
                  "Authorization": `Bearer ${access_token}`,
                  "Content-Type": "application/json",
                  "auth-token": authToken,
                },
              });
              const updatedInfo = await userInfo();
              setInfo(updatedInfo);
            },
            onError: (err) => {
              console.error("PayPal Error:", err);
              setError(err.message);
            }
          }).render(paypalRef.current);
        }
      } catch (error) {
        console.error("PayPal setup error:", error);
        setError(error.message);
      }
    };

    setupPayPal();
  }, [scriptLoaded]);

  const triggerPayPalFlow = () => {
    if (!scriptLoaded) {
      setScriptLoaded(false); // This will trigger the script to reload
      return;
    }

    if (paypalRef.current) {
      const paypalButton = paypalRef.current.querySelector('button');
      if (paypalButton) {
        paypalButton.click();
      } else {
        console.error("PayPal button not found");
        setScriptLoaded(false); // Force reload if button not found
      }
    } else {
      console.error("PayPal container reference not found");
      setScriptLoaded(false); // Force reload if container not found
    }
  };

  return (
    <div className="flex flex-col gap-4 w-[95%] m-auto my-6">
      {/* Credit Card Modal */}
      <div
        className={`${connectCardState ? "block" : "hidden"} fixed top-0 left-0 z-50 w-screen h-screen flex justify-center items-center`}
      >
        <div
          className="absolute w-full h-full z-10"
          onClick={() => setConnectCardState(false)}
        ></div>
        <ConnectCard setConnect={setConnectCardState} />
      </div>

      <div className="">
        <p className={`text-lightGrey font-pop mb-4 ${theme === "dark" && "text-white"}`}>
          Portfolio
        </p>
        <div className={`rounded-2xl py-6 px-6 ${theme === "dark" ? "bg-black text-white" : "bg-white"}`}>
          <div className="flex flex-col font-pop gap-5">
            {loading ? (
              <div className="text-center">Loading payment information...</div>
            ) : error ? (
              <div className="text-center text-red-500">{error}</div>
            ) : (
              <>
                {/* PayPal Section */}
                {info?.AttachedPaypal ? (
                  <div className="flex flex-row border-2 items-center justify-between border-[#E9E9E9] py-2 md:py-4 px-2 md:px-6 rounded-lg gap-2">
                    <div className="flex flex-row gap-2 items-center">
                      <img
                        src="../../Assets/Accounts/Paypal.png"
                        alt="PayPal"
                        className="w-6 md:w-8 h-6 md:h-8"
                      />
                      <p className="text-base md:text-lg font-medium">
                        PayPal account ({info.AttachedPaypal.Email.substring(0, 4)} ****)
                      </p>
                    </div>
                    <img
                      src="../../Assets/Prop/Delete.png"
                      alt="Delete"
                      className="w-6 h-6 cursor-pointer hover:opacity-70 transition-opacity"
                      onClick={() => handleDeletePayment('paypal')}
                    />
                  </div>
                ) : (
                  <div
                    className="relative flex flex-row border-2 items-center border-[#E9E9E9] py-4 px-6 rounded-lg gap-2 cursor-pointer hover:bg-gray-50 transition-colors"
                    onClick={triggerPayPalFlow}
                  >
                    <div className="flex flex-row gap-2 items-center">
                      <img
                        src="../../Assets/Accounts/Paypal.png"
                        alt="PayPal"
                        className="w-6 md:w-8 h-6 md:h-8"
                      />
                      <p className="text-base md:text-lg font-medium">
                        Connect PayPal account
                      </p>
                    </div>
                    {/* Invisible PayPal button container */}
                    <div
                      ref={paypalRef}
                      className="absolute inset-0 opacity-0"
                    ></div>
                  </div>
                )}

                {/* Credit Card Section */}
                {info?.AttachedCard ? (
                  <div className="flex flex-row border-2 items-center justify-between border-[#E9E9E9] py-2 md:py-4 px-2 md:px-6 rounded-lg gap-2">
                    <div className="flex flex-row gap-2 items-center">
                      <img
                        src="../../Assets/Accounts/card.png"
                        alt="Credit Card"
                        className="w-6 md:w-8 h-6 md:h-8"
                      />
                      <p className="text-base md:text-lg font-medium">
                        Credit card (**** {info.AttachedCard.LastDigits})
                      </p>
                    </div>
                    <img
                      src="../../Assets/Prop/Delete.png"
                      alt="Delete"
                      className="w-6 h-6 cursor-pointer hover:opacity-70 transition-opacity"
                      onClick={() => handleDeletePayment('card')}
                    />
                  </div>
                ) : (
                  <div
                    className="flex flex-row border-2 items-center justify-between border-[#E9E9E9] py-2 md:py-4 px-2 md:px-6 rounded-lg gap-2 cursor-pointer hover:bg-gray-50 transition-colors"
                    onClick={() => setConnectCardState(true)}
                  >
                    <div className="flex flex-row gap-2 items-center">
                      <img
                        src="../../Assets/Accounts/card.png"
                        alt="Credit Card"
                        className="w-6 md:w-8 h-6 md:h-8"
                      />
                      <p className="text-base md:text-lg font-medium">
                        Connect Credit Card
                      </p>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>

      <div className="">
        <p
          className={`text-lightGrey font-pop mb-4    ${theme == "dark" && " text-white"
            }  `}
        >
          Push notifications
        </p>
        <div
          className={` rounded-2xl  py-6 px-6    ${theme == "dark" ? "bg-black text-white" : "bg-white "
            }`}
        >
          <div className="flex flex-col font-pop gap-4">
            <div className="flex flex-row justify-between gap-4 py-4 border-b-2 border-light">
              <h3 className="font-bold">Inbox messages</h3>
              <label className="inline-flex items-center relative cursor-pointer">
                <input type="checkbox" value="" className="sr-only peer" />
                <div className="relative w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 dark:peer-focus:ring-blue-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-primarygreen/60 peer-checked:bg-primarygreen"></div>
              </label>
            </div>
            <div className="flex flex-row justify-between gap-4 py-4 border-b-2 border-light">
              <h3 className="font-bold">Order messages</h3>
              <label className="inline-flex items-center relative cursor-pointer">
                <input type="checkbox" value="" className="sr-only peer" />
                <div className="relative w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 dark:peer-focus:ring-blue-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-primarygreen/60 peer-checked:bg-primarygreen"></div>
              </label>
            </div>
            <div className="flex flex-row justify-between gap-4 py-4 border-b-2 border-light">
              <h3 className="font-bold">Order updates</h3>
              <label className="inline-flex items-center relative cursor-pointer">
                <input type="checkbox" value="" className="sr-only peer" />
                <div className="relative w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 dark:peer-focus:ring-blue-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-primarygreen/60 peer-checked:bg-primarygreen"></div>
              </label>
            </div>
            <div className="flex flex-row justify-between gap-4 py-4 border-b-2 border-light">
              <h3 className="font-bold">My account</h3>
              <label className="inline-flex items-center cursor-pointer">
                <input type="checkbox" value="" className="sr-only peer" />
                <div className="relative w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 dark:peer-focus:ring-blue-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-primarygreen/60 peer-checked:bg-primarygreen"></div>
              </label>
            </div>
          </div>
        </div>
      </div>

      <div className="">
        <p
          className={` text-lightGrey font-pop mb-4    ${theme == "dark" && " text-white"
            }  `}
        >
          Appearance
        </p>
        <div
          className={` rounded-2xl  py-6 px-6    ${theme == "dark" ? "bg-black text-white" : "bg-white "
            }`}
        >
          <div className="flex flex-col font-pop gap-4">
            <div className="flex flex-row justify-between gap-4 py-4 border-b-2 border-light">
              <h3 className="font-bold">Light</h3>
              <label className="inline-flex items-center relative cursor-pointer">
                <input
                  type="checkbox"
                  value=""
                  checked={theme === "light"}
                  onChange={() => handleThemeChange("light")}
                  className="sr-only peer"
                />
                <div className="relative w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 dark:peer-focus:ring-blue-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-primarygreen/60 peer-checked:bg-primarygreen"></div>
              </label>
            </div>
            <div className="flex flex-row justify-between gap-4 py-4 border-b-2 border-light">
              <h3 className="font-bold">Dark</h3>
              <label className="inline-flex items-center relative cursor-pointer">
                <input
                  type="checkbox"
                  checked={theme === "dark"}
                  onChange={() => handleThemeChange("dark")}
                  value=""
                  className="sr-only peer"
                />
                <div className="relative w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 dark:peer-focus:ring-blue-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-primarygreen/60 peer-checked:bg-primarygreen"></div>
              </label>
            </div>
          </div>
        </div>
      </div>

      <button
        className={`border-2 border-lightGrey my-4 rounded-full py-2 px-4 font-pop text-lg font-medium w-fit
              ${theme == "dark" ? "bg-black text-white" : "bg-white "}`}
      >
        Log Out
      </button>
    </div>

  );
};

export default Setting;
